import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, Clock, Send } from "lucide-react";

export default function Questions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [sending, setSending] = useState(false);
  const [answerDrafts, setAnswerDrafts] = useState({});

  const isAdmin = user?.role === "admin";

  const load = () => api.get("/questions").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const payload = { text: text.trim() };
      if (chapterId) payload.chapter_id = parseInt(chapterId, 10);
      await api.post("/questions", payload);
      toast.success("Question sent to your instructor");
      setText(""); setChapterId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const answer = async (id) => {
    const a = (answerDrafts[id] || "").trim();
    if (!a) return;
    try {
      await api.post(`/questions/${id}/answer`, { answer: a });
      toast.success("Answer sent");
      setAnswerDrafts(d => ({ ...d, [id]: "" }));
      load();
    } catch (e) { toast.error("Reply failed"); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3">{isAdmin ? "Instructor inbox" : "Ask your instructor"}</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">
          {isAdmin ? "Student questions." : "Talk to a human."}
        </h1>

        {!isAdmin && (
          <Card className="p-6 mb-10">
            <div className="label-caps mb-2">New question</div>
            <p className="text-sm text-muted-foreground mb-4">Use this when the AI Tax Teacher isn't clicking. A human instructor will reply.</p>
            <div className="space-y-3">
              <Input
                data-testid="question-chapter-input"
                type="number"
                min="1"
                max="13"
                value={chapterId}
                onChange={e => setChapterId(e.target.value)}
                placeholder="Chapter # (optional)"
                className="max-w-[220px]"
              />
              <Textarea
                data-testid="question-text-input"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="What's confusing you?"
                rows={4}
              />
              <Button onClick={send} disabled={sending || !text.trim()} data-testid="send-question-btn">
                <Send size={14} className="mr-2" /> {sending ? "Sending…" : "Send to instructor"}
              </Button>
            </div>
          </Card>
        )}

        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No questions yet.</div>
        ) : (
          <div className="space-y-4" data-testid="questions-list">
            {items.map(q => (
              <Card key={q.id} className="p-6" data-testid={`question-${q.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-caps flex items-center gap-2">
                    {q.status === "answered" ? <CheckCircle2 size={14} className="text-primary" /> : <Clock size={14} className="text-accent" />}
                    <span>{q.status === "answered" ? "Answered" : "Awaiting reply"}</span>
                    {q.chapter_id && <span className="text-muted-foreground">· Ch {q.chapter_id}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</div>
                </div>
                {isAdmin && (
                  <div className="text-xs text-muted-foreground mb-2">
                    from <strong className="text-foreground">{q.student_name}</strong> ({q.student_email})
                  </div>
                )}
                <div className="text-base leading-relaxed whitespace-pre-wrap">{q.text}</div>

                {q.answer ? (
                  <div className="mt-4 border-l-4 border-primary pl-4 py-2">
                    <div className="flex items-center gap-3 mb-1">
                      {q.answered_by_photo && (
                        <img src={`${API}/files/${q.answered_by_photo}`} alt="" className="h-8 w-8 rounded-full object-cover" data-testid={`answer-photo-${q.id}`} />
                      )}
                      <div className="label-caps">Reply · {q.answered_by}</div>
                    </div>
                    <div className="text-base leading-relaxed whitespace-pre-wrap">{q.answer}</div>
                    {q.answered_by_signature && (
                      <div className="text-xs text-muted-foreground mt-3 whitespace-pre-wrap italic">{q.answered_by_signature}</div>
                    )}
                    {q.answered_by_logo && (
                      <img src={`${API}/files/${q.answered_by_logo}`} alt="" className="h-8 mt-3 opacity-80" data-testid={`answer-logo-${q.id}`} />
                    )}
                  </div>
                ) : isAdmin ? (
                  <div className="mt-4 space-y-2">
                    <Textarea
                      data-testid={`answer-input-${q.id}`}
                      value={answerDrafts[q.id] || ""}
                      onChange={e => setAnswerDrafts(d => ({ ...d, [q.id]: e.target.value }))}
                      placeholder="Reply to this student…"
                      rows={3}
                    />
                    <Button size="sm" onClick={() => answer(q.id)} data-testid={`send-answer-btn-${q.id}`}>
                      Send reply
                    </Button>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
